import { SupabaseClient } from '@supabase/supabase-js';
import { QueryCache } from '../cache/query-cache';

// Tipos para el monitoreo
export interface QueryMetrics {
  table: string;
  operation: 'select' | 'insert' | 'update' | 'delete' | 'count';
  startTime: number;
  endTime: number;
  duration: number;
  success: boolean;
  error?: any;
  rowCount?: number;
  querySize?: number;
}

export interface ConnectionStatus {
  isConnected: boolean;
  lastChecked: number;
  latency: number;
  error?: string;
}

export interface MonitoringStats {
  totalQueries: number;
  successfulQueries: number;
  failedQueries: number;
  averageQueryTime: number;
  slowestQueries: QueryMetrics[];
  connectionStatus: ConnectionStatus;
  cacheStats: {
    hits: number;
    misses: number;
    size: number;
  };
}

// Clase para monitorear operaciones de Supabase
export class SupabaseMonitor {
  private static instance: SupabaseMonitor;
  private supabase: SupabaseClient;
  private queryCache: QueryCache;
  private metrics: QueryMetrics[] = [];
  private connectionStatus: ConnectionStatus = {
    isConnected: false,
    lastChecked: 0,
    latency: 0,
  };
  private cacheStats = {
    hits: 0,
    misses: 0,
  };
  private maxMetricsToStore = 1000;
  private slowQueryThreshold = 1000; // 1 segundo
  private connectionCheckInterval = 5 * 60 * 1000; // 5 minutos
  private lastConnectionCheck = 0;

  private constructor(supabase: SupabaseClient) {
    this.supabase = supabase;
    this.queryCache = QueryCache.getInstance();
    
    // Iniciar verificación periódica de conexión
    this.startConnectionMonitoring();
  }

  public static getInstance(supabase: SupabaseClient): SupabaseMonitor {
    if (!SupabaseMonitor.instance) {
      SupabaseMonitor.instance = new SupabaseMonitor(supabase);
    }
    return SupabaseMonitor.instance;
  }

  // Registrar una consulta para monitoreo
  public logQuery(metrics: QueryMetrics): void {
    // Añadir métricas a la lista
    this.metrics.push(metrics);
    
    // Limitar el número de métricas almacenadas
    if (this.metrics.length > this.maxMetricsToStore) {
      this.metrics = this.metrics.slice(-this.maxMetricsToStore);
    }
    
    // Registrar consultas lentas
    if (metrics.duration > this.slowQueryThreshold) {
      console.warn(`⚠️ Consulta lenta detectada: ${metrics.table}.${metrics.operation} (${metrics.duration}ms)`);
    }
    
    // Registrar errores
    if (!metrics.success) {
      console.error(`❌ Error en consulta: ${metrics.table}.${metrics.operation}`, metrics.error);
    }
  }

  // Registrar un hit de caché
  public logCacheHit(): void {
    this.cacheStats.hits++;
  }

  // Registrar un miss de caché
  public logCacheMiss(): void {
    this.cacheStats.misses++;
  }

  // Obtener estadísticas de monitoreo
  public getStats(): MonitoringStats {
    const now = Date.now();
    
    // Verificar conexión si ha pasado suficiente tiempo
    if (now - this.lastConnectionCheck > this.connectionCheckInterval) {
      this.checkConnection();
    }
    
    // Calcular estadísticas
    const successfulQueries = this.metrics.filter(m => m.success).length;
    const failedQueries = this.metrics.filter(m => !m.success).length;
    const totalQueries = this.metrics.length;
    
    // Calcular tiempo promedio de consulta
    const totalDuration = this.metrics.reduce((sum, m) => sum + m.duration, 0);
    const averageQueryTime = totalQueries > 0 ? totalDuration / totalQueries : 0;
    
    // Obtener las consultas más lentas
    const slowestQueries = [...this.metrics]
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 5);
    
    // Obtener estadísticas de caché
    const cacheStats = this.queryCache.getCacheStats();
    
    return {
      totalQueries,
      successfulQueries,
      failedQueries,
      averageQueryTime,
      slowestQueries,
      connectionStatus: this.connectionStatus,
      cacheStats: {
        hits: this.cacheStats.hits,
        misses: this.cacheStats.misses,
        size: cacheStats.size,
      },
    };
  }

  // Verificar el estado de la conexión
  private async checkConnection(): Promise<void> {
    const startTime = Date.now();
    this.lastConnectionCheck = startTime;
    
    try {
      const { error } = await this.supabase.from('companies').select('count');
      const endTime = Date.now();
      
      this.connectionStatus = {
        isConnected: !error,
        lastChecked: endTime,
        latency: endTime - startTime,
        error: error ? error.message : undefined,
      };
      
      if (error) {
        console.error(`❌ Error de conexión con Supabase: ${error.message}`);
      } else {
        console.log(`✅ Conexión con Supabase verificada (latencia: ${endTime - startTime}ms)`);
      }
    } catch (err) {
      const endTime = Date.now();
      
      this.connectionStatus = {
        isConnected: false,
        lastChecked: endTime,
        latency: endTime - startTime,
        error: err instanceof Error ? err.message : 'Error desconocido',
      };
      
      console.error(`❌ Error al verificar conexión con Supabase:`, err);
    }
  }

  // Iniciar monitoreo de conexión
  private startConnectionMonitoring(): void {
    // Verificar conexión inicial
    this.checkConnection();
    
    // Configurar verificación periódica
    setInterval(() => {
      this.checkConnection();
    }, this.connectionCheckInterval);
  }

  // Obtener métricas para una tabla específica
  public getTableMetrics(table: string): QueryMetrics[] {
    return this.metrics.filter(m => m.table === table);
  }

  // Obtener métricas para una operación específica
  public getOperationMetrics(operation: 'select' | 'insert' | 'update' | 'delete' | 'count'): QueryMetrics[] {
    return this.metrics.filter(m => m.operation === operation);
  }

  // Limpiar métricas antiguas (más de 24 horas)
  public cleanOldMetrics(): void {
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    this.metrics = this.metrics.filter(m => m.endTime > oneDayAgo);
  }
} 